// ONB-PARENT: the pure part of the first-run wizard (no DOM, no network): the gate truth table, the state shape, the transitions.
// State lives in profiles.onboarding (jsonb): { v:1, status:'pending'|'in_progress'|'done'|'skipped', step:<id>, steps:{<id>:'done'|'skipped'}, resumes:<n>, at:<iso> }.
// 'pending' (the server default of a NEW row) = due. NULL = an existing account: never shown the wizard (no backfill).

export const STATE_V = 1;
export const MAX_BYTES = 4096;          // the profiles_onboarding_shape_chk limit (ONB-0)
const RESULTS = ['done', 'skipped'];

const isObj = x => x && typeof x === 'object' && !Array.isArray(x);

// THE ONE PLACE that decides whether an account that has NOT started the wizard is due for it (planning R-0142: the DEFAULT gate).
// profiles.onboarding gets a server-side DEFAULT '{"v":1,"status":"pending"}' for NEW rows only (ONB-0); every existing row stays NULL. So: status 'pending' = a new parent, due; NULL = an existing account, never due.
// No clock, no release-time constant. force = QA override (an @resend.dev account with localStorage hud_onb_force=1) that makes a NULL account due; it never overrides a stored state.
export function isOnboardingDue(profile, force) {
  const o = profile && profile.onboarding;
  if (isObj(o) && o.v === STATE_V && o.status === 'pending') return { due: true, reason: 'new' };
  if (o == null) return force ? { due: true, reason: 'forced' } : { due: false, reason: 'existing_account' };
  return { due: false, reason: 'not_fresh' };
}

// Is this account in scope of the wizard at all (used to suppress the three competing first-run prompts as well)?
// ctx: { session, force, hasPendingInvite, steps (count of registered steps) }
export function gate(ctx) {
  const s = ctx && ctx.session;
  if (!s || !s.id) return { show: false, reason: 'no_session' };
  if (s.id === 'demo') return { show: false, reason: 'demo' };
  if (s.isAdmin) return { show: false, reason: 'admin' };
  if (s.role !== 'parent') return { show: false, reason: 'not_parent' };
  if (!ctx.steps) return { show: false, reason: 'no_steps' };
  const o = s.onboarding;
  if (o != null && (!isObj(o) || o.v !== STATE_V || !['pending', 'in_progress', 'done', 'skipped'].includes(o.status))) return { show: false, reason: 'unknown_state' };   // an unknown shape is never "fresh": do not show
  if (o && o.status !== 'in_progress' && o.status !== 'pending') return { show: false, reason: o.status };
  if (!o || o.status === 'pending') {
    const d = isOnboardingDue(s, ctx.force);
    if (!d.due) return { show: false, reason: d.reason };
    return ctx.hasPendingInvite ? { show: false, reason: 'pending_invite', defer: true } : { show: true, reason: d.reason, resume: false };
  }
  // in_progress: resume ONCE at the stored step; abandoned a second time = skipped for good (never nag)
  if ((o.resumes || 0) >= 1) return { show: false, reason: 'resume_expired', expire: true };
  return ctx.hasPendingInvite ? { show: false, reason: 'pending_invite', defer: true } : { show: true, reason: 'resume', resume: true };
}

// R-0150: the 2-second "Add a Co-Parent" popup stays for every parent EXCEPT while the wizard is due (pending / in_progress, it has the co-parent step) or done for this account.
// An existing parent (NULL), and an account whose wizard was skipped for good, keep the popup (and its Skip, #304).
export function coParentPopupSuppressed(onboarding) {
  return !!(isObj(onboarding) && onboarding.v === STATE_V && ['pending', 'in_progress', 'done'].includes(onboarding.status));
}

// The scope question without the "right now" conditions: true while the wizard still has something to show this account (drives the suppression of the old prompts).
export function pending(ctx) {
  const g = gate(Object.assign({}, ctx, { hasPendingInvite: false }));
  return !!g.show;
}

export function fresh(firstStep, nowIso) { return { v: STATE_V, status: 'in_progress', step: firstStep, steps: {}, resumes: 0, at: nowIso }; }

export function resumed(state, nowIso) { return Object.assign({}, state, { resumes: (state.resumes || 0) + 1, at: nowIso }); }

// A step finished (result 'done' after its commit succeeded, or 'skipped'). A step already committed stays 'done' (Back, then Skip never undoes a commit).
export function record(state, id, result, nextId, nowIso) {
  if (!RESULTS.includes(result)) throw new Error('bad result ' + result);
  const prev = state.steps && state.steps[id];
  const steps = Object.assign({}, state.steps, { [id]: prev === 'done' ? 'done' : result });
  return Object.assign({}, state, { status: 'in_progress', step: nextId, steps, at: nowIso });
}

export function finish(state, nowIso) { return Object.assign({}, state, { status: 'done', step: null, at: nowIso }); }
export function expire(state, nowIso) { return Object.assign({}, state, { status: 'skipped', at: nowIso }); }

export function sizeOk(state) { return new TextEncoder().encode(JSON.stringify(state)).length <= MAX_BYTES; }

// Back/Next geometry: the sign of the horizontal slide for one navigation. The outgoing step moves to -sign*24px, the incoming starts at +sign*24px.
// forward in LTR: old goes left, new enters from the right; Back is the exact mirror; in RTL both mirror.
export function slideSign(forward, rtl) { return (forward ? 1 : -1) * (rtl ? -1 : 1); }

// Which steps does this account see, in order (steps without applicable() are always shown).
export function visibleSteps(registry, ctx) {
  return registry.filter(st => !st.applicable || st.applicable(ctx));
}

// Where to start/resume: the stored step if it is still visible, else the first step that has no recorded result, else the first.
export function startIndex(visible, state) {
  if (!state) return 0;
  const i = visible.findIndex(s => s.id === state.step);
  if (i >= 0) return i;
  const j = visible.findIndex(s => !(state.steps && state.steps[s.id]));
  return j >= 0 ? j : 0;
}
